import React, { PropsWithChildren, useState } from 'react'
import { createContext } from 'lib/createContext'
import { useRuleQuery } from 'queries/wage/useRuleQuery'

type Rate = NonNullable<
  ReturnType<typeof useRuleQuery>['data']
>['rates'][number]

type Workflow = {
  isOpen: boolean
  open: () => void
  close: () => void
}

type RuleDetailWorkflowsContextState = {
  editRuleFlow: Workflow
  addRateFlow: Workflow
  editRateFlow: Omit<Workflow, 'open'> & {
    selectedRate: Rate | null
    open: (rate: Rate) => void
  }
  deleteRateConfirmFlow: Omit<Workflow, 'open'> & {
    selectedRate: Rate | null
    open: (rate: Rate) => void
  }
  attachJurisdictionFlow: Workflow
  addExclusionFlow: Workflow
}

const [Context, useRuleDetailWorkflowsContextValue] =
  createContext<RuleDetailWorkflowsContextState>({
    name: 'RuleDetailWorkflowsContext',
  })

export const RuleDetailWorkflowsProvider = ({
  children,
}: PropsWithChildren) => {
  const [isEditRuleOpen, setIsEditRuleOpen] = useState(false)
  const [isAddRateOpen, setIsAddRateOpen] = useState(false)
  const [isEditRateOpen, setIsEditRateOpen] = useState(false)
  const [selectedRate, setSelectedRate] = useState<Rate | null>(null)
  const [isDeleteRateConfirmOpen, setIsDeleteRateConfirmOpen] = useState(false)
  const [rateToDelete, setRateToDelete] = useState<Rate | null>(null)
  const [isAttachJurisdictionOpen, setIsAttachJurisdictionOpen] =
    useState(false)
  const [isAddExclusionOpen, setIsAddExclusionOpen] = useState(false)

  const value: RuleDetailWorkflowsContextState = {
    editRuleFlow: {
      isOpen: isEditRuleOpen,
      open: () => setIsEditRuleOpen(true),
      close: () => setIsEditRuleOpen(false),
    },
    addRateFlow: {
      isOpen: isAddRateOpen,
      open: () => setIsAddRateOpen(true),
      close: () => setIsAddRateOpen(false),
    },
    editRateFlow: {
      isOpen: isEditRateOpen,
      selectedRate,
      open: (rate: Rate) => {
        setSelectedRate(rate)
        setIsEditRateOpen(true)
      },
      close: () => {
        setIsEditRateOpen(false)
        setSelectedRate(null)
      },
    },
    deleteRateConfirmFlow: {
      isOpen: isDeleteRateConfirmOpen,
      selectedRate: rateToDelete,
      open: (rate: Rate) => {
        setRateToDelete(rate)
        setIsDeleteRateConfirmOpen(true)
      },
      close: () => {
        setIsDeleteRateConfirmOpen(false)
        setRateToDelete(null)
      },
    },
    attachJurisdictionFlow: {
      isOpen: isAttachJurisdictionOpen,
      open: () => setIsAttachJurisdictionOpen(true),
      close: () => setIsAttachJurisdictionOpen(false),
    },
    addExclusionFlow: {
      isOpen: isAddExclusionOpen,
      open: () => setIsAddExclusionOpen(true),
      close: () => setIsAddExclusionOpen(false),
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useRuleDetailWorkflowsContextValue }
